import { useState } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Badge,
  Button,
  Checkbox,
  Modal,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { MetaTags } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_VOE_CONTACT_EMAIL_USAGE,
  REMOVE_VOE_CONTACT_EMAIL,
  REPLACE_VOE_CONTACT_EMAIL,
} from 'src/graphql/voeContacts.graphql'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import { formatSnakeValue } from 'src/lib/formatters'

type UsageRow = Record<string, string | number | boolean | null>

const SECTIONS: {
  key: string
  title: string
  blurb: string
  columns: { label: string; field: string }[]
}[] = [
  {
    key: 'contacts',
    title: 'Contacts',
    blurb:
      'The saved contacts we use when a verification needs someone to reach at this employer.',
    columns: [
      { label: 'Name', field: 'contactName' },
      { label: 'Employer', field: 'employerName' },
      { label: 'DOT', field: 'dotNumber' },
      { label: 'Phone', field: 'phoneNumber' },
      { label: 'Source', field: 'source' },
      { label: 'Times seen', field: 'occurrenceCount' },
    ],
  },
  {
    key: 'carriers',
    title: 'Carriers',
    blurb:
      "The employer's own address on file. Used when none of their contacts have one.",
    columns: [
      { label: 'Employer', field: 'employerName' },
      { label: 'DOT', field: 'dotNumber' },
    ],
  },
  {
    key: 'verificationsOfEmployment',
    title: 'Verifications still in play',
    blurb:
      'Verifications that could still contact this person. Completed, canceled and declined ones are left as they are — they record who was actually contacted.',
    columns: [
      { label: 'Employer', field: 'employerName' },
      { label: 'DOT', field: 'dotNumber' },
      { label: 'Driver', field: 'driverName' },
      { label: 'Client', field: 'clientCompanyName' },
      { label: 'Status', field: 'status' },
    ],
  },
  {
    key: 'employmentExperiences',
    title: 'Employment history entries',
    blurb:
      'What drivers entered in their own applications. Replacing an address updates these too; removing a contact leaves them exactly as the driver wrote them.',
    columns: [
      { label: 'Employer', field: 'employerName' },
      { label: 'DOT', field: 'dotNumber' },
      { label: 'Driver', field: 'driverName' },
    ],
  },
]

const AdminVoEContactsPage = () => {
  const [searchInput, setSearchInput] = useState('')
  const [replacement, setReplacement] = useState('')
  const [blockOnReplace, setBlockOnReplace] = useState(false)
  const [blockOnRemove, setBlockOnRemove] = useState(true)
  const [keptNumbers, setKeptNumbers] = useState<string[]>([])
  const [names, setNames] = useState<Record<string, string>>({})
  const [confirmOpened, confirmHandlers] = useDisclosure(false)
  const [removeOpened, removeHandlers] = useDisclosure(false)

  const [runSearch, { data, loading, refetch }] = useLazyQuery(
    GET_VOE_CONTACT_EMAIL_USAGE,
    { fetchPolicy: 'network-only' }
  )

  const [replaceEmail, { loading: replacing }] = useMutation(
    REPLACE_VOE_CONTACT_EMAIL,
    {
      onCompleted: ({ replaceVoEContactEmail: result }) => {
        const changed =
          result.contactsUpdated +
          result.carriersUpdated +
          result.verificationsOfEmploymentUpdated +
          result.employmentExperiencesUpdated
        const mergedNote = result.contactsMerged
          ? `, ${result.contactsMerged} duplicate contact${
              result.contactsMerged === 1 ? '' : 's'
            } merged`
          : ''
        toast(
          `Updated ${changed} record${changed === 1 ? '' : 's'}${mergedNote}`
        )
        confirmHandlers.close()
        setSearchInput(result.newEmail)
        setReplacement('')
        refetch?.({ email: result.newEmail })
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )

  const [removeContact, { loading: removing }] = useMutation(
    REMOVE_VOE_CONTACT_EMAIL,
    {
      onCompleted: ({ removeVoEContactEmail: result }) => {
        const parts = [
          `${result.contactsArchived} contact${
            result.contactsArchived === 1 ? '' : 's'
          } archived`,
        ]
        if (result.verificationsCleared) {
          parts.push(
            `${result.verificationsCleared} verification${
              result.verificationsCleared === 1 ? '' : 's'
            } cleared`
          )
        }
        if (result.carriersCleared) {
          parts.push('carrier address cleared')
        }
        toast(parts.join(', '))
        removeHandlers.close()
        refetch?.({ email: result.email })
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )

  const usage = data?.voeContactEmailUsage
  const searched = usage?.email

  const numbersOnContacts: string[] = [
    ...new Set(
      (usage?.contacts ?? []).flatMap((contact) =>
        [contact.phoneNumber, ...(contact.alternativePhones ?? [])].filter(
          Boolean
        )
      )
    ),
  ]
  const takenNumbers = numbersOnContacts.filter(
    (number) => !keptNumbers.includes(number)
  )
  const survives = (contact) =>
    [contact.phoneNumber, ...(contact.alternativePhones ?? [])]
      .filter(Boolean)
      .some((number) => keptNumbers.includes(number))
  const survivors = (usage?.contacts ?? []).filter(survives)
  const nameFor = (contact) =>
    names[contact.contactId] ?? contact.contactName ?? ''
  const renameArgs = (rows) =>
    rows
      .filter((contact) => names[contact.contactId] !== undefined)
      .map((contact) => ({
        contactId: contact.contactId,
        contactName: names[contact.contactId] || null,
      }))
  const nameInput = (contact) => (
    <div key={contact.contactId} className="flex flex-col gap-1">
      <span className="text-xs text-doubleNickel-gray-500">
        {[
          contact.employerName ?? 'Unknown carrier',
          contact.phoneNumber,
          contact.source ? formatSnakeValue(contact.source) : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>
      <TextInput
        id={`contact-name-${contact.contactId}`}
        aria-label={`Name for ${contact.employerName ?? contact.contactId}`}
        value={nameFor(contact)}
        onChange={(event) => {
          const { value } = event.currentTarget
          setNames((current) => ({
            ...current,
            [contact.contactId]: value,
          }))
        }}
      />
    </div>
  )

  const onSearch = () => {
    const email = searchInput.trim()
    if (!email) return
    runSearch({ variables: { email } })
  }

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-6">
      <MetaTags
        title="Employment Verification Contacts"
        description="Admin VoE contact directory"
      />

      <div className="flex flex-col gap-1">
        <div className="text-lg font-semibold text-doubleNickel-gray-900">
          Contacts
        </div>
        <div className="max-w-3xl text-sm text-doubleNickel-gray-600">
          Look up an employer contact by email to see everywhere it is used. You
          can correct it in one go, or take it out of use. These records are
          shared by every client, so a change here applies to all of them.
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <TextInput
          id="voe-contact-search"
          label="Email in use"
          placeholder="name@carrier.com"
          className="min-w-[20rem]"
          value={searchInput}
          onChange={(event) => setSearchInput(event.currentTarget.value)}
          onKeyDown={(event) => event.key === 'Enter' && onSearch()}
        />
        <Button
          onClick={onSearch}
          loading={loading}
          disabled={!searchInput.trim()}
        >
          Search
        </Button>
      </div>

      {usage && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-2 text-sm text-doubleNickel-gray-700">
            <span className="font-medium">{usage.totalRecords}</span>
            <span>
              record{usage.totalRecords === 1 ? '' : 's'} using{' '}
              <span className="font-mono">{searched}</span>
            </span>
          </div>

          {SECTIONS.map((section) => {
            const rows: UsageRow[] = usage[section.key] ?? []
            return (
              <div key={section.key} className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold text-doubleNickel-gray-900">
                    {section.title}
                  </div>
                  <Badge size="sm" variant="light">
                    {rows.length}
                  </Badge>
                </div>
                <div className="max-w-3xl text-xs text-doubleNickel-gray-500">
                  {section.blurb}
                </div>
                {rows.length > 0 && (
                  <div className="overflow-x-auto rounded-md border border-doubleNickel-gray-200">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-doubleNickel-gray-50 text-doubleNickel-gray-600">
                        <tr>
                          {section.columns.map((column) => (
                            <th
                              key={column.field}
                              className="px-3 py-2 font-medium"
                            >
                              {column.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr
                            key={String(
                              row.contactId ??
                                row.carrierId ??
                                row.voeId ??
                                row.employmentExperienceId ??
                                index
                            )}
                            className="border-t border-doubleNickel-gray-200"
                          >
                            {section.columns.map((column) => (
                              <td key={column.field} className="px-3 py-2">
                                {row[column.field] ?? '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          })}

          {usage.totalRecords > 0 && (
            <div className="flex flex-row flex-wrap items-center gap-3 border-t border-doubleNickel-gray-200 pt-5">
              <div className="flex flex-row items-center gap-1.5">
                <Button onClick={confirmHandlers.open}>Replace contact</Button>
                <Tooltip
                  withArrow
                  multiline
                  w={260}
                  position="top"
                  label="Correct the address everywhere it is used. Verifications already completed keep the address they were contacted on."
                >
                  <span
                    aria-label="What replacing does"
                    className="flex cursor-help items-center"
                  >
                    <IconInfoCircle className="h-4 w-4 fill-none stroke-doubleNickel-gray-400" />
                  </span>
                </Tooltip>
              </div>

              <div className="flex flex-row items-center gap-1.5">
                <Button
                  variant="outline"
                  color="red"
                  onClick={removeHandlers.open}
                >
                  Remove contact
                </Button>
                <Tooltip
                  withArrow
                  multiline
                  w={260}
                  position="top"
                  label="Take the contact out of use without replacing it. What drivers entered in their own applications stays exactly as they wrote it."
                >
                  <span
                    aria-label="What removing does"
                    className="flex cursor-help items-center"
                  >
                    <IconInfoCircle className="h-4 w-4 fill-none stroke-doubleNickel-gray-400" />
                  </span>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal
        opened={confirmOpened}
        onClose={confirmHandlers.close}
        title="Replace this email everywhere?"
        size="lg"
        centered
      >
        <div className="flex flex-col gap-4 text-sm">
          <div className="flex flex-col gap-1">
            <span className="text-doubleNickel-gray-600">Replacing</span>
            <span className="font-mono">{searched}</span>
          </div>
          <TextInput
            id="voe-contact-replacement"
            label="Replace with"
            placeholder="verifications@carrier.com"
            data-autofocus
            value={replacement}
            onChange={(event) => setReplacement(event.currentTarget.value)}
          />
          <div className="text-doubleNickel-gray-600">
            {usage?.totalRecords} record
            {usage?.totalRecords === 1 ? '' : 's'} will change, for every
            client. Verifications that are already completed, canceled or
            declined keep the address they were contacted on.
          </div>
          <Checkbox
            checked={blockOnReplace}
            onChange={(event) => setBlockOnReplace(event.currentTarget.checked)}
            label="Suppress the old address"
            description="Stops the old address ever being used again, even if a driver enters it in a later application. Leave this off if you are just fixing a typo."
          />

          {(usage?.contacts ?? []).length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-doubleNickel-gray-600">
                Contact names — correct them alongside the address, or leave
                them as they are
              </span>
              {(usage?.contacts ?? []).map(nameInput)}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="subtle" onClick={confirmHandlers.close}>
              Cancel
            </Button>
            <Button
              loading={replacing}
              disabled={!replacement.trim()}
              onClick={() =>
                replaceEmail({
                  variables: {
                    currentEmail: searched,
                    newEmail: replacement.trim(),
                    suppressPrevious: blockOnReplace,
                    contactNames: renameArgs(usage?.contacts ?? []),
                  },
                })
              }
            >
              Replace
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        opened={removeOpened}
        onClose={removeHandlers.close}
        title="Remove this contact?"
        size="xl"
        centered
      >
        <div className="flex flex-col gap-4 text-sm">
          <div className="flex flex-col gap-1">
            <span className="text-doubleNickel-gray-600">Removing</span>
            <span className="font-mono">{searched}</span>
          </div>
          <div className="flex flex-col gap-2 text-doubleNickel-gray-600">
            <span>
              The address is removed from {usage?.contacts.length} contact
              {usage?.contacts.length === 1 ? '' : 's'}, for every client.
            </span>
            <span>
              Any verification that could still contact them loses the address,
              including ones already in progress. Completed, canceled and
              declined verifications keep it, and what the driver entered in
              their application is unchanged.
            </span>
          </div>
          <Checkbox
            checked={blockOnRemove}
            onChange={(event) => setBlockOnRemove(event.currentTarget.checked)}
            label="Suppress this address"
            description="Stops it ever being used again. Without this, a driver entering it in a future application brings it straight back. You can undo it under Suppressed."
          />

          {(usage?.contacts ?? []).length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-doubleNickel-gray-600">
                Phone numbers on these contacts. Untick any you want to keep
                using.
              </span>
              <div className="flex flex-col divide-y divide-doubleNickel-gray-200 rounded-md border border-doubleNickel-gray-200">
                {(usage?.contacts ?? []).map((contact) => {
                  const numbers = [
                    contact.phoneNumber,
                    ...(contact.alternativePhones ?? []),
                  ].filter(Boolean)
                  const kept = survives(contact)
                  return (
                    <div
                      key={contact.contactId}
                      className="flex flex-row flex-wrap items-start gap-4 p-3"
                    >
                      <div className="flex min-w-[14rem] flex-1 flex-col gap-2">
                        <span className="font-medium text-doubleNickel-gray-900">
                          {contact.employerName ?? 'Unknown carrier'}
                          {contact.dotNumber
                            ? ` · DOT ${contact.dotNumber}`
                            : ''}
                        </span>
                        {numbers.length === 0 ? (
                          <span className="text-xs text-doubleNickel-gray-500">
                            No phone on this contact
                          </span>
                        ) : (
                          numbers.map((number) => (
                            <Checkbox
                              key={`${contact.contactId}-${number}`}
                              label={number}
                              checked={!keptNumbers.includes(number)}
                              onChange={(event) => {
                                const { checked } = event.currentTarget
                                setKeptNumbers((current) =>
                                  checked
                                    ? current.filter((k) => k !== number)
                                    : [...current, number]
                                )
                              }}
                            />
                          ))
                        )}
                      </div>
                      <div className="min-w-[14rem] flex-1">
                        {kept ? (
                          <TextInput
                            id={`contact-name-${contact.contactId}`}
                            label="Name"
                            aria-label={`Name for ${
                              contact.employerName ?? contact.contactId
                            }`}
                            value={nameFor(contact)}
                            onChange={(event) => {
                              const { value } = event.currentTarget
                              setNames((current) => ({
                                ...current,
                                [contact.contactId]: value,
                              }))
                            }}
                          />
                        ) : (
                          <span className="text-xs text-doubleNickel-gray-500">
                            Nothing left to reach them on — this contact is
                            taken out of use.
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="subtle" onClick={removeHandlers.close}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={removing}
              onClick={() =>
                removeContact({
                  variables: {
                    email: searched,
                    suppress: blockOnRemove,
                    alsoRemove: takenNumbers.map((value) => ({
                      contactType: 'PHONE',
                      value,
                    })),
                    contactNames: renameArgs(survivors),
                  },
                })
              }
            >
              Remove contact
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AdminVoEContactsPage
